// The interview itself.
//
// WHAT THIS SCREEN HAS TO DO
// Hold one nervous person's attention for half an hour, make the current
// question unmissable, and never once leave them unsure whether the thing is
// working. Everything below serves one of those three.
//
// LAYOUT REASONING
// The question owns the left column at a controlled reading measure, because it
// is the only thing on screen that must land first time. The two faces of the
// conversation stack on the right — Maya above, the candidate below — which puts
// the person you are talking to and the person you are being recorded as in the
// same glance. Video panels are context, not content; sizing them like content
// pulls attention off the question.
//
// On a phone the columns stack question-first. A candidate on mobile is the
// common case here, not the edge case.
//
// PROGRESS IS SHOWN, NOT COUNTED DOWN
// A ticking count of questions remaining invites people to rush the last few.
// The rail shows where you are; the number is small and secondary.
//
// THE TIMER IS A SAFETY NET AND IS DRAWN LIKE ONE
// It stays quiet until the final thirty seconds, then turns amber. Making it
// prominent throughout turns a generous limit into a stopwatch, and this
// product scores hesitation.

import React from "react";
import {
  Box,
  Button,
  Typography,
  Stack,
  Alert,
  Tooltip,
  IconButton,
  LinearProgress,
} from "@mui/material";
import VideocamIcon from "@mui/icons-material/Videocam";
import StopIcon from "@mui/icons-material/Stop";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import ReplayIcon from "@mui/icons-material/Replay";
import CheckIcon from "@mui/icons-material/Check";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import InterviewerOrb from "./InterviewerOrb";
import { FONT, MONO, dark as C, radius, motion, injectKeyframes } from "./theme";

injectKeyframes();

// What kind of question this is, in the candidate's language rather than ours.
// "SCREENING" and "DEEPEN" are our vocabulary; a candidate reads them as jargon
// and, worse, as a hint about how they are being judged.
const KIND = {
  WARMUP: "To begin",
  SCREENING: "About your experience",
  COMPETENCY: "About your experience",
  RESUME: "About your background",
  DEEPEN: "Following up",
  LOGISTICS: "Just the practical bit",
};

function mmss(total) {
  const m = Math.floor(Math.max(0, total) / 60);
  const s = Math.max(0, total) % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

const Panel = ({ children, sx, ...rest }) => (
  <Box
    sx={{
      background: C.panel,
      border: `1px solid ${C.hair}`,
      borderRadius: radius.md,
      overflow: "hidden",
      ...sx,
    }}
    {...rest}
  >
    {children}
  </Box>
);

const InterviewStage = ({
  session,
  questions,
  current,
  index,
  recording,
  secondsLeft,
  uploading,
  uploadFailed,
  videoRef,
  stream,
  onStart,
  onStop,
  onRetryUpload,
  onSkip,
  canSkip = true,
  prepLeft,
  holding,
  idle,
  onHold,
  onResumeCountdown,
  pendingShort,
  onKeepShort,
  onRedo,
  speech,
  interviewerName,
}) => {
  const total = questions.length || 1;
  const answered = index;
  const limit = current?.max_duration_sec || 120;
  const running = recording && secondsLeft > 0;
  const nearlyUp = running && secondsLeft <= 30;
  const isLast = index >= questions.length - 1;

  const orbState = recording
    ? "listening"
    : speech?.speaking || speech?.loading
      ? "speaking"
      : uploading
        ? "thinking"
        : "asking";

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: C.bg,
        color: C.ink,
        fontFamily: FONT,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* ── Progress rail ───────────────────────────────────────────────── */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 3,
          bgcolor: "rgba(10,15,20,.86)",
          backdropFilter: "blur(14px)",
          borderBottom: `1px solid ${C.hair}`,
        }}
      >
        <Box sx={{ maxWidth: 1280, mx: "auto", px: { xs: 2.5, md: 4 }, py: 1.75 }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Typography sx={{ ...({ fontFamily: MONO }), fontSize: 11, letterSpacing: ".1em", color: C.faint }}>
              {String(index + 1).padStart(2, "0")}
              <Box component="span" sx={{ opacity: 0.5 }}> / {String(total).padStart(2, "0")}</Box>
            </Typography>

            {/* Segments rather than a bar: a bar says "how much is left", and a
                candidate who can see the end coming starts rationing answers. */}
            <Stack direction="row" spacing={0.5} sx={{ flex: 1 }}>
              {questions.map((q, i) => (
                <Box
                  key={q.index}
                  sx={{
                    flex: 1,
                    height: 3,
                    borderRadius: 2,
                    bgcolor:
                      i < answered ? C.act : i === answered ? C.maya : C.line,
                    opacity: i <= answered ? 1 : 0.5,
                    transition: `background-color ${motion.slow}, opacity ${motion.slow}`,
                  }}
                />
              ))}
            </Stack>

            {uploading && (
              <Stack direction="row" spacing={0.75} alignItems="center">
                <CloudUploadOutlinedIcon sx={{ fontSize: 15, color: C.faint }} />
                <Typography sx={{ fontSize: 11.5, color: C.faint }}>Saving</Typography>
              </Stack>
            )}
          </Stack>
        </Box>
      </Box>

      {/* ── Body ────────────────────────────────────────────────────────── */}
      {/*
          Four named areas rather than two columns, because the phone and the
          desktop want genuinely different orders and neither may duplicate a
          node to get it.

          On a phone the candidate must be able to see THEMSELVES — they are
          about to be recorded, and a stacked column put the question, then a
          300px Maya, then the camera, so the one thing they needed to check
          was below the fold. Now: question and a small Maya share the top row,
          the camera takes the middle at a size worth looking at, and the
          controls sit under it without scrolling.

          Single <video> and single orb across both layouts, deliberately. Two
          <video> nodes is the black-preview bug this file already carries a
          note about; two orbs is two AudioContexts open while recording, which
          InterviewerOrb goes out of its way to avoid on a phone.
      */}
      <Box
        sx={{
          flex: 1,
          maxWidth: 1280,
          width: "100%",
          mx: "auto",
          px: { xs: 1.75, md: 4 },
          py: { xs: 1.75, md: 5 },
          display: "grid",
          columnGap: { xs: 1.5, md: 5 },
          rowGap: { xs: 1.75, md: 5 },
          gridTemplateColumns: {
            xs: "minmax(0,1fr) auto",
            lg: "minmax(0,1.35fr) minmax(320px,.65fr)",
          },
          gridTemplateAreas: {
            xs: `"question maya" "stage stage" "controls controls"`,
            lg: `"question maya" "controls stage"`,
          },
          alignItems: "start",
        }}
      >
        {/* QUESTION */}
        <Box
          key={current?.index}
          sx={{
            gridArea: "question",
            animation: `hRise .4s ${motion.base.split(" ")[1]} both`,
            minWidth: 0,
          }}
        >
          <Typography
            sx={{
              fontFamily: MONO,
              fontSize: { xs: 9.5, md: 11 },
              letterSpacing: ".12em",
              textTransform: "uppercase",
              color: C.maya,
              mb: { xs: 0.75, md: 2 },
            }}
          >
            {KIND[current?.category] || "Question"}
          </Typography>

          <Typography
            sx={{
              // Much smaller on a phone. At 23px a two-line question became
              // four and pushed the camera off the screen — the question has
              // to be readable, not headline-sized, and it is also being read
              // aloud.
              fontSize: { xs: 15.5, md: 31 },
              fontWeight: { xs: 600, md: 650 },
              lineHeight: { xs: 1.34, md: 1.38 },
              letterSpacing: { xs: "-.01em", md: "-.02em" },
              // Held to a comfortable measure on desktop. A question spanning a
              // full desktop width is measurably harder to read, and this is
              // the one thing that must land first time. On a phone the column
              // is already narrow, so capping it only wastes the width.
              maxWidth: { xs: "none", md: "26ch" },
              color: C.ink,
            }}
          >
            {current?.question}
          </Typography>

          {/* Voice controls. Hidden while recording — replaying a question into
              an open microphone is exactly the bleed this design avoids. */}
          {speech?.supported && !recording && (
            <Stack direction="row" spacing={0.5} sx={{ mt: 2.5, ml: -1 }}>
              <Tooltip title={speech.enabled ? "Turn off Maya's voice" : "Turn on Maya's voice"}>
                <IconButton
                  size="small"
                  onClick={() => speech.setEnabled(!speech.enabled)}
                  sx={{ color: speech.enabled ? C.maya : C.faint }}
                >
                  {speech.enabled ? <VolumeUpIcon fontSize="small" /> : <VolumeOffIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
              {speech.enabled && (
                <Tooltip title="Hear the question again">
                  <IconButton
                    size="small"
                    disabled={speech.loading}
                    onClick={() =>
                      speech.speak(current?.question, current?.index, current?.qid)
                    }
                    sx={{ color: C.faint }}
                  >
                    <ReplayIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          )}
        </Box>

        {/* CONTROLS — under the question on desktop, under the camera on a
            phone, so the candidate checks their framing and then records
            without the two ever being separated by a scroll. */}
        <Box sx={{ gridArea: "controls", minWidth: 0 }}>
          <Box
            sx={{
              mt: { xs: 0, md: 3.5 },
              pt: { xs: 0, md: 3 },
              borderTop: { xs: "none", md: `1px solid ${C.hair}` },
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 2,
            }}
          >
            {/* ── One state, one instruction, one obvious action ───────────
                Candidates were getting lost in a screen that waited silently
                for them to notice a button. The commonest failures were both
                failures of the interface, not of the person:

                  forgot to press record, and answered to a camera that was
                  not recording;
                  pressed record and stop in quick succession by reflex, which
                  advanced the interview and cost them the question.

                So: the screen always states what is happening and what happens
                next, the countdown removes the record button from the critical
                path, and a suspiciously short answer is confirmed rather than
                taken at face value. */}

            {pendingShort ? (
              /* Held, not uploaded. Nothing has been sent, so "answer again"
                 is a genuinely fresh attempt rather than an edit. */
              <Stack spacing={1.5} sx={{ width: "100%" }}>
                <Alert
                  severity="warning"
                  icon={<WarningAmberRoundedIcon />}
                  sx={{
                    borderRadius: radius.md,
                    bgcolor: "rgba(251,191,36,.1)",
                    color: C.ink,
                    border: `1px solid rgba(251,191,36,.25)`,
                    "& .MuiAlert-icon": { color: C.caution },
                  }}
                >
                  {/* Two very different problems, and the advice differs. A
                      slip needs "try again"; a silent recording needs the
                      candidate to look at their microphone first, or they will
                      re-record silence and lose the question twice. */}
                  {pendingShort.heardSpeech === false ? (
                    <>
                      <strong>We couldn't hear anything in that recording.</strong>{" "}
                      Check your microphone isn't muted — including any switch on a
                      headset — and answer again. Nothing has been sent yet.
                    </>
                  ) : (
                    <>
                      That answer was only{" "}
                      {Math.max(1, Math.round(pendingShort.durationMs / 1000))} second
                      {Math.round(pendingShort.durationMs / 1000) === 1 ? "" : "s"} long.
                      If you stopped by mistake, answer it again — nothing has been
                      sent yet.
                    </>
                  )}
                </Alert>
                <Stack direction="row" spacing={1.5} sx={{ flexWrap: "wrap", gap: 1.5 }}>
                  <Button
                    onClick={onRedo}
                    startIcon={<ReplayIcon />}
                    sx={{
                      fontFamily: FONT, textTransform: "none", fontWeight: 700,
                      fontSize: 15.5, px: 3.5, py: 1.4, borderRadius: radius.md,
                      bgcolor: C.act, color: "#04231f",
                      "&:hover": { bgcolor: "#5eead4" },
                    }}
                  >
                    Answer again
                  </Button>
                  <Button
                    onClick={onKeepShort}
                    sx={{
                      fontFamily: FONT, textTransform: "none", fontWeight: 600,
                      fontSize: 15, px: 3, py: 1.4, borderRadius: radius.md,
                      color: C.body, border: `1px solid ${C.line}`,
                    }}
                  >
                    No, that's my answer
                  </Button>
                </Stack>
              </Stack>
            ) : recording ? (
              <Stack direction="row" spacing={2} alignItems="center" sx={{ flexWrap: "wrap", gap: 2 }}>
                <Button
                  onClick={onStop}
                  startIcon={isLast ? <CheckIcon /> : <StopIcon />}
                  sx={{
                    fontFamily: FONT,
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: 15.5,
                    px: 3.5,
                    py: 1.4,
                    borderRadius: radius.md,
                    bgcolor: "#f43f5e",
                    color: "#fff",
                    "&:hover": { bgcolor: "#e11d48" },
                  }}
                >
                  {isLast ? "Finish interview" : "Done — next question"}
                </Button>
                {/* Unmistakable, because the single most costly confusion was
                    not knowing whether it was recording. */}
                <Stack direction="row" spacing={1} alignItems="center">
                  <Box
                    sx={{
                      width: 10, height: 10, borderRadius: "50%",
                      bgcolor: "#f43f5e",
                      animation: "hBreathe 1.2s ease-in-out infinite",
                    }}
                  />
                  <Typography sx={{ fontFamily: FONT, fontSize: 14, fontWeight: 700, color: "#f43f5e" }}>
                    Recording
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: MONO,
                      fontSize: 13,
                      color: nearlyUp ? C.caution : C.faint,
                      transition: `color ${motion.base}`,
                    }}
                  >
                    {mmss(secondsLeft)} left
                  </Typography>
                </Stack>
              </Stack>
            ) : (
              <Stack spacing={1.25} sx={{ width: "100%" }}>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ flexWrap: "wrap", gap: 1.5 }}>
                  <Button
                    onClick={onStart}
                    disabled={uploading}
                    startIcon={<VideocamIcon />}
                    sx={{
                      fontFamily: FONT,
                      textTransform: "none",
                      fontWeight: 700,
                      fontSize: 15.5,
                      px: 3.5,
                      py: 1.4,
                      borderRadius: radius.md,
                      bgcolor: C.act,
                      color: "#04231f",
                      "&:hover": { bgcolor: "#5eead4" },
                      "&.Mui-disabled": { bgcolor: C.line, color: C.faint },
                    }}
                  >
                    {prepLeft > 0 ? `Start now (${prepLeft})` : "Start answering"}
                  </Button>

                  {prepLeft > 0 && (
                    <Button
                      onClick={onHold}
                      sx={{
                        fontFamily: FONT, textTransform: "none", fontWeight: 600,
                        fontSize: 14.5, px: 2.5, py: 1.3, borderRadius: radius.md,
                        color: C.body, border: `1px solid ${C.line}`,
                      }}
                    >
                      I need a moment
                    </Button>
                  )}

                  {holding && (
                    <Button
                      onClick={onResumeCountdown}
                      sx={{
                        fontFamily: FONT, textTransform: "none", fontWeight: 600,
                        fontSize: 14.5, px: 2.5, py: 1.3, borderRadius: radius.md,
                        color: C.body, border: `1px solid ${C.line}`,
                      }}
                    >
                      Restart the countdown
                    </Button>
                  )}

                  <Typography sx={{ fontFamily: MONO, fontSize: 13, color: C.faint }}>
                    up to {mmss(limit)}
                  </Typography>
                </Stack>

                {/* Says what is about to happen, in words, before it happens.
                    A countdown nobody has explained is its own confusion. */}
                <Typography sx={{ fontFamily: FONT, fontSize: 13.5, color: prepLeft > 0 ? C.maya : C.faint }}>
                  {prepLeft > 0
                    ? `Recording starts on its own in ${prepLeft} second${prepLeft === 1 ? "" : "s"} — you don't need to press anything.`
                    : holding
                      ? "Take your time. Press start when you're ready."
                      : "Getting ready…"}
                </Typography>

                {idle && (
                  <Alert
                    severity="info"
                    sx={{
                      borderRadius: radius.md,
                      bgcolor: "rgba(45,212,191,.08)",
                      color: C.ink,
                      border: `1px solid ${C.line}`,
                      "& .MuiAlert-icon": { color: C.maya },
                    }}
                  >
                    Still here? Nothing is being recorded yet — press{" "}
                    <strong>Start answering</strong> when you're ready.
                  </Alert>
                )}
              </Stack>
            )}
          </Box>

          {/* ── Passing on a question ─────────────────────────────────────
              Offered only when not recording, and never on a logistics
              question — the server refuses those, because an unanswered hard
              gate is treated as "do not fail" and skipping would make it a
              free pass.

              Quiet on purpose: a button as prominent as Record invites people
              to take the easy road on a question they could have attempted.
              But it has to EXIST, because without it somebody who does not
              know an answer records three seconds of silence instead — and
              silence is what the transcriber used to turn into an invented
              answer they were then scored on. */}
          {/* Hidden while a short answer is being confirmed: two decisions on
              screen at once is how somebody picks the wrong one. */}
          {!recording && !pendingShort && canSkip && (
            <Button
              onClick={onSkip}
              disabled={uploading}
              sx={{
                mt: 1.5,
                ml: -1,
                fontFamily: FONT,
                textTransform: "none",
                fontWeight: 600,
                fontSize: 13.5,
                color: C.faint,
                "&:hover": { color: C.ink, bgcolor: "transparent" },
              }}
            >
              I don't know this one — skip it
            </Button>
          )}

          <Typography sx={{ mt: 2, fontSize: 13, color: C.faint, maxWidth: "48ch", lineHeight: 1.7 }}>
            {pendingShort
              ? "Nothing has been sent yet. Answering again replaces it completely — there is no penalty for a second try."
              : recording
                ? "Take your time. Press done when you've finished — there's no advantage to using the whole clock."
                : "Answer in your own words, out loud. Once you move on you can't come back to this question."}
          </Typography>

          {uploadFailed && (
            <Alert
              severity="warning"
              // Only offered when the failure still holds a recording to send.
              // A failed skip has nothing to retry, and a Try again that
              // re-enters the upload path would tell the candidate their
              // recording arrived empty — for a question they chose not to
              // record.
              action={
                uploadFailed.blob ? (
                  <Button onClick={onRetryUpload} sx={{ fontWeight: 700 }}>Try again</Button>
                ) : null
              }
              sx={{
                mt: 3,
                borderRadius: radius.md,
                bgcolor: "rgba(251,191,36,.1)",
                color: C.ink,
                border: `1px solid rgba(251,191,36,.25)`,
                "& .MuiAlert-icon": { color: C.caution },
              }}
            >
              {uploadFailed.message || "That answer didn't save."}
            </Alert>
          )}
        </Box>

        {/* MAYA — a presence, not a panel. Small and cornered on a phone. */}
        <Panel
          sx={{
            gridArea: "maya",
            width: { xs: 92, lg: "auto" },
            height: { xs: 92, lg: 300 },
            flex: "none",
          }}
        >
          {/*
              The orb's internals are laid out at a fixed 152px, so it is
              scaled rather than resized — one instance, one AudioContext, and
              no second code path through a component that manages a live audio
              graph. transformOrigin keeps it centred in the smaller panel.
          */}
          <Box
            sx={{
              width: "100%",
              height: "100%",
              transform: { xs: "scale(.52)", lg: "none" },
              transformOrigin: "center center",
            }}
          >
            <InterviewerOrb
              state={orbState}
              stream={stream}
              label={interviewerName || "Maya"}
            />
          </Box>
        </Panel>

        {/* STAGE — the candidate's own camera */}
        <Stack spacing={2} sx={{ gridArea: "stage", minWidth: 0, width: "100%" }}>
          <Panel
            sx={{
              position: "relative",
              // Portrait and tall on a phone, because that is the shape of both
              // the screen and the person. Capped in viewport units so the
              // controls underneath stay reachable without scrolling.
              aspectRatio: { xs: "auto", lg: "4 / 3" },
              height: { xs: "min(56vh, 460px)", lg: "auto" },
              bgcolor: "#000",
            }}
          >
            <video
              ref={videoRef}
              autoPlay
              muted
              playsInline
              style={{ width: "100%", height: "100%", objectFit: "cover", transform: "scaleX(-1)" }}
            />
            <Box
              sx={{
                position: "absolute", left: 12, bottom: 12,
                display: "flex", alignItems: "center", gap: 0.75,
                px: 1.25, py: 0.5,
                borderRadius: radius.pill,
                bgcolor: "rgba(0,0,0,.55)",
                backdropFilter: "blur(8px)",
              }}
            >
              <Box
                sx={{
                  width: 6, height: 6, borderRadius: "50%",
                  bgcolor: recording ? "#f43f5e" : C.faint,
                  animation: recording ? "hBreathe 1.2s ease-in-out infinite" : "none",
                }}
              />
              <Typography sx={{ fontFamily: MONO, fontSize: 10.5, letterSpacing: ".08em", color: C.body }}>
                {recording ? "RECORDING" : "YOU"}
              </Typography>
            </Box>
          </Panel>

          {uploading && (
            <LinearProgress
              sx={{
                height: 2, borderRadius: 2, bgcolor: C.line,
                "& .MuiLinearProgress-bar": { bgcolor: C.maya },
              }}
            />
          )}
        </Stack>
      </Box>
    </Box>
  );
};

export default InterviewStage;
